// Progressive map reveal for the minimap. The party sees every square within
// SIGHT_RADIUS that has a clear line of sight; walls and closed doors block
// the view (but are themselves seen). Seen squares stay on the map.

import { isSeeThroughTile } from './combat/monsters';

export const SIGHT_RADIUS = 3;

const key = (x, y) => x + ',' + y;

// Walk a straight line between square centres; everything strictly between
// the two ends must be see-through
function hasLineOfSight(map, [px, py], [tx, ty]) {
    const steps = Math.ceil(Math.max(Math.abs(tx - px), Math.abs(ty - py)) * 4);
    for (let i = 1; i < steps; i++) {
        const x = Math.round(px + ((tx - px) * i) / steps);
        const y = Math.round(py + ((ty - py) * i) / steps);
        if ((x === px && y === py) || (x === tx && y === ty)) continue;
        if (!isSeeThroughTile(map[y][x])) return false;
    }
    return true;
}

// Squares the party can currently see, as a Set of "x,y" keys
export function visibleTiles(map, coords, radius = SIGHT_RADIUS) {
    const [px, py] = coords;
    const seen = new Set([key(px, py)]);
    for (let y = py - radius; y <= py + radius; y++) {
        for (let x = px - radius; x <= px + radius; x++) {
            if (y < 0 || y >= map.length || x < 0 || x >= map[0].length) continue;
            // Round-ish sight: skip the far corners of the square area
            if ((x - px) ** 2 + (y - py) ** 2 > (radius + 0.5) ** 2) continue;
            if (hasLineOfSight(map, coords, [x, y])) seen.add(key(x, y));
        }
    }
    return seen;
}

// A fresh explored grid (nothing seen yet) for a map
export function createExplored(map) {
    return map.map((row) => row.map(() => false));
}

// Mark what the party can see from `coords` as explored. Returns the same
// grid if nothing new was seen, so state updates can be skipped.
export function revealAround(explored, map, coords) {
    let next = explored;
    visibleTiles(map, coords).forEach((k) => {
        const [x, y] = k.split(',').map(Number);
        if (next[y][x]) return;
        if (next === explored) next = explored.map((row) => row);
        if (next[y] === explored[y]) next[y] = [...explored[y]];
        next[y][x] = true;
    });
    return next;
}
